import map from 'lodash/map';
import times from 'lodash/times';
import filter from 'lodash/filter';
import getSamples from 'lodash/sampleSize';

import { observable, action } from 'mobx';

import { mean, sampleStandardDeviation as stdev } from 'simple-statistics';

import {
  areParametersTheSame,

  isParameterNumerical,
  isParameterCategorical
} from 'utilities/parameters';

const samplingDistributions = {
  parameter: {
    name: null,
    type: null,
    values: []
  },

  parameterLevel: null,

  sampleSize: null,
  numberOfSamples: null,

  samples: null,
  selectedSampleId: 0,

  get doSamplesExist () {
    return this.samples !== null;
  },

  get isParameterNumerical () {
    return isParameterNumerical(
      this.parameter
    );
  },

  get isParameterCategorical () {
    return isParameterCategorical(
      this.parameter
    );
  },

  get means () {
    if (this.doSamplesExist && this.isParameterNumerical) {
      return this.samples.map(mean);
    }

    return null;
  },

  get proportions () {
    if (this.doSamplesExist && this.isParameterCategorical && this.parameterLevel) {
      return map(this.samples, sample => filter(sample, value => value === this.parameterLevel).length / sample.length);
    }

    return null;
  },

  get dots () {
    if (this.doSamplesExist) {
      return this.isParameterCategorical
        ? this.proportions
        : this.means;
    }

    return null;
  },

  get dotsMean () {
    if (this.dots && this.dots.length > 0) {
      return mean(this.dots);
    }

    return null;
  },

  get dotsStdev () {
    if (this.dots && this.dots.length >= 2) {
      return stdev(this.dots);
    }

    return null;
  },

  get dotsAmount () {
    return this.dots && this.dots.length;
  },

  get selectedSample () {
    return this.samples && this.samples[this.selectedSampleId];
  },

  get selectedSampleSummary () {
    if (this.selectedSample) {
      return {
        mean: mean(this.selectedSample),
        stdev: this.selectedSample.length >= 2 ? stdev(this.selectedSample) : 'N/A',
        amount: this.selectedSample.length
      };
    }

    return null;
  },

  get originalSampleSummary () {
    if (this.parameter && this.parameter.name) {
      return {
        mean: mean(this.parameter.values),
        stdev: this.parameter.values.length >= 2 ? stdev(this.parameter.values) : 'N/A',
        amount: this.parameter.values.length
      };
    }

    return null;
  },

  get samplesSpecifications () {
    const predicate = value => value === this.parameterLevel;

    const parameterValues = this.parameter.values;
    const generatedValues = this.selectedSample;

    return {
      totalAmount: {
        original: parameterValues.length,
        bootstrapped: generatedValues.length
      },
      levelAmount: {
        original: parameterValues.filter(predicate).length,
        bootstrapped: generatedValues.filter(predicate).length
      }
    };
  },

  generateSamples: action(
    'GENERATE NEW SAMPLES',

    function (parameter, options) {
      const samples = times(options.numberOfSamples, () =>
        getSamples(parameter.values, options.sampleSize)
      );

      const sameParameter = areParametersTheSame(this.parameter, parameter);

      const sameParameterLevel = this.parameterLevel === options.parameterLevel;
      const sameSampleSize = this.sampleSize === options.sampleSize;

      if (sameParameter && sameParameterLevel && sameSampleSize) {
        this.samples = this.samples.concat(samples);
      } else {
        this.samples = samples;
        this.selectedSampleId = 0;
      }

      if (!sameParameter) {
        this.parameter = parameter;
      }

      this.sampleSize = options.sampleSize;
      this.parameterLevel = options.parameterLevel;
      this.numberOfSamples = options.numberOfSamples;
    }
  ),

  reset: action(
    'RESET GENERATED SAMPLES',

    function () {
      this.parameter = {
        name: null,
        type: null,
        values: []
      };

      this.parameterLevel = null;

      this.sampleSize = null;
      this.numberOfSamples = null;

      this.samples = null;
      this.selectedSampleId = 0;
    }
  )
};

export const createSamplingDistributionsObservable = () =>
  observable(samplingDistributions);
